<template>
    <div class="eval-stats">
        <div class="stats-top-bar">
            <button class="stats-back-btn" @click="$router.back()">
                <v-icon size="16">mdi-chevron-left</v-icon>
                {{ $t('common.back') }}
            </button>

            <segmented-tabs v-if="!loading" class="stats-view-tabs" :options="viewOptions" :value="view"
                @change="view = $event" />
        </div>

        <skeleton-loader v-if="loading" type="eval-stats" />

        <template v-else>
            <div class="stats-top">
                <div class="group-profile-card">
                    <div class="group-card-banner">
                        <div class="group-card-icon">{{ teamInitials }}</div>
                    </div>
                    <div class="group-card-body">
                        <div class="group-card-name">{{ team ? team.name : '' }}</div>

                        <div class="group-card-field">
                            <span class="group-card-field-label">{{ $t('common.group') }}</span>
                            <v-menu offset-y left>
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="group-card-select" v-bind="attrs" v-on="on">
                                        {{ group ? group.name : $t('common.all') }}
                                        <span class="chart-select-caret">▾</span>
                                    </button>
                                </template>
                                <v-list dense>
                                    <v-list-item link @click="selectGroup(null)">
                                        <v-list-item-title>{{ $t('common.all') }}</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-for="g in groups" :key="g.id" link @click="selectGroup(g)">
                                        <v-list-item-title>{{ g.name }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>

                        <div v-if="group || isCompare" class="group-card-field">
                            <span class="group-card-field-label">{{ $t('common.student') }}</span>
                            <v-menu offset-y left>
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="group-card-select" v-bind="attrs" v-on="on">
                                        {{ student ? student.name : $t('common.all') }}
                                        <span class="chart-select-caret">▾</span>
                                    </button>
                                </template>
                                <v-list dense>
                                    <v-list-item link @click="student = null">
                                        <v-list-item-title>{{ $t('common.all') }}</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-for="s in students" :key="s.id" link @click="student = s">
                                        <v-list-item-title>{{ s.name }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>

                        <div class="group-card-field">
                            <span class="group-card-field-label">{{ $t('common.members') }}</span>
                            <span class="group-card-field-value">
                                {{ $tc('common.students_count', memberCount, { n: memberCount }) }}
                            </span>
                        </div>
                        <div class="group-card-field">
                            <span class="group-card-field-label">{{ $t('common.last_evaluation') }}</span>
                            <span class="group-card-field-value">{{ lastEvaluationDate }}</span>
                        </div>
                        <div class="group-card-field">
                            <span class="group-card-field-label">{{ $t('common.evaluations') }}</span>
                            <span class="group-card-field-value value-accent">
                                {{ recordsCount }} {{ $tc('common.records', recordsCount) }}
                            </span>
                        </div>
                        <button v-if="hasData" class="stats-export-btn" @click="exportExcel">
                            <v-icon size="14">mdi-tray-arrow-down</v-icon>
                            {{ $t('common.export') }}
                        </button>
                    </div>
                </div>

                <div v-if="isCompare && !compareRows.length" class="stats-empty-card">
                    <v-icon size="40" color="txtMuted">mdi-account-search-outline</v-icon>
                    <p>{{ student ? $t('common.no_values_student') : $t('common.select_student_compare') }}</p>
                </div>

                <div v-else-if="isCompare" class="stats-table-card">
                    <div class="stats-table-scroll">
                        <table class="stats-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('common.evaluation') }}</th>
                                    <th>{{ $t('common.unit_measure') }}</th>
                                    <th>{{ $t('common.student') }}</th>
                                    <th>{{ compareScopeLabel }}</th>
                                    <th>{{ $t('common.difference_group') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in compareRows" :key="row.id">
                                    <td>
                                        <div class="metric-name-cell"><strong>{{ row.name }}</strong></div>
                                    </td>
                                    <td>{{ row.unit }}</td>
                                    <td>{{ row.student }}</td>
                                    <td>{{ row.group != null ? row.group : '—' }}</td>
                                    <td>
                                        <div class="diff-cell">
                                            <span :class="compareDiffClass(row)">{{ compareDiffText(row) }}</span>
                                            <div class="diff-bar-wrap">
                                                <div class="diff-bar-fill" :style="compareDiffBarStyle(row)" />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-else-if="!hasData" class="stats-empty-card">
                    <v-icon size="40" color="txtMuted">mdi-database-off-outline</v-icon>
                    <p>{{ $t('common.no_values_team') }}</p>
                </div>

                <div v-else class="stats-table-card">
                    <div class="stats-table-scroll">
                        <table class="stats-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('common.evaluation') }}</th>
                                    <th>{{ $t('common.unit_measure') }}</th>
                                    <th>{{ $t('common.actual') }}</th>
                                    <th>{{ $t('common.difference_ideal') }}</th>
                                    <th>{{ $t('common.best') }}</th>
                                    <th>{{ $t('common.worst') }}</th>
                                    <th>{{ $t('common.average') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in items" :key="item.id">
                                    <td>
                                        <div class="metric-name-cell"><strong>{{ item.name }}</strong></div>
                                    </td>
                                    <td>{{ item.unit }}</td>
                                    <td>{{ item.actual }}</td>
                                    <td>
                                        <div class="diff-cell">
                                            <span :class="diffClass(item)">{{ diffText(item) }}</span>
                                            <div class="diff-bar-wrap">
                                                <div class="diff-bar-fill" :style="diffBarStyle(item)" />
                                            </div>
                                        </div>
                                    </td>
                                    <td>{{ item.best }}</td>
                                    <td>{{ item.worst }}</td>
                                    <td>{{ item.average }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="isCompare && compareRows.length" class="stats-charts">
                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.evolution') }}</span>
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <button class="chart-select" v-bind="attrs" v-on="on">
                                    {{ currentCompareConfig ? currentCompareConfig.name : '' }}
                                    <span class="chart-select-caret">▾</span>
                                </button>
                            </template>
                            <v-list dense>
                                <v-list-item v-for="metric in compareConfigWithData" :key="metric.id" link
                                    color="primary"
                                    :input-value="currentCompareConfig && currentCompareConfig.id === metric.id"
                                    @click="currentCompareConfig = metric">
                                    <v-list-item-title>{{ metric.name }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <div class="chart-body">
                        <evaluation-dates-chart :values="compareStudentValuesDates" :compare="compareGroupValuesDates"
                            :compare-label="compareScopeLabel" style="height: 210px;" />
                        <div class="chart-legend-row">
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-success" />
                                {{ student ? student.name : $t('common.student') }}
                            </div>
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-secondary" />
                                {{ compareScopeLabel }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.comparative') }}</span>
                    </div>
                    <div class="chart-body">
                        <div class="cmp-bars">
                            <div v-for="row in compareRows" :key="row.id" class="cmp-bar-row">
                                <div class="cmp-bar-head">
                                    <span class="cmp-bar-name">{{ row.name }}</span>
                                    <span class="cmp-bar-values">
                                        <b>{{ row.student }}</b> / {{ row.group != null ? row.group : '—' }}
                                    </span>
                                </div>
                                <div class="cmp-bar-track">
                                    <div class="cmp-bar-fill" :style="cmpBarStyle(row, 'student')" />
                                    <div class="cmp-bar-marker" :style="cmpBarStyle(row, 'group')" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.profile_evaluations') }}</span>
                    </div>
                    <div class="chart-body">
                        <radar-chart :labels="compareNames" :datasets="compareDatasets" style="height: 230px;" />
                        <div class="chart-legend-row">
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-primary" />
                                {{ student ? student.name : $t('common.student') }}
                            </div>
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-accent" />
                                {{ compareScopeLabel }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="!isCompare && hasData" class="stats-charts">
                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.historical') }}</span>
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <button class="chart-select" v-bind="attrs" v-on="on">
                                    {{ currentConfigDates ? currentConfigDates.name : '' }}
                                    <span class="chart-select-caret">▾</span>
                                </button>
                            </template>
                            <v-list dense>
                                <v-list-item v-for="metric in configWithData" :key="metric.id" link color="primary"
                                    :input-value="currentConfigDates && currentConfigDates.id === metric.id"
                                    @click="currentConfigDates = metric">
                                    <v-list-item-title>{{ metric.name }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <div class="chart-body">
                        <evaluation-dates-chart :values="currentValuesDates"
                            :ideal="currentConfigDates ? currentConfigDates.value : null" style="height: 210px;" />
                        <div class="chart-legend-row">
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-success" />
                                {{ $t('common.actual') }}
                            </div>
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-accent" />
                                {{ $t('common.ideal') }}: {{ currentConfigDates ? currentConfigDates.value : '' }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.summary_evaluations') }}</span>
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <button class="chart-select" v-bind="attrs" v-on="on">
                                    {{ currentConfig ? currentConfig.name : '' }}
                                    <span class="chart-select-caret">▾</span>
                                </button>
                            </template>
                            <v-list dense>
                                <v-list-item v-for="metric in configWithData" :key="metric.id" link color="primary"
                                    :input-value="currentConfig && currentConfig.id === metric.id"
                                    @click="currentConfig = metric">
                                    <v-list-item-title>{{ metric.name }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <div class="chart-body">
                        <evaluation-summary-chart v-if="currentItem" :item="currentItem" />
                    </div>
                </div>

                <div class="chart-card">
                    <div class="chart-header">
                        <span class="chart-title">{{ $t('common.profile_evaluations') }}</span>
                    </div>
                    <div class="chart-body">
                        <radar-chart :labels="evaluationsName" :datasets="evaluationData" style="height: 230px;" />
                        <div class="chart-legend-row">
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-primary" />
                                {{ $t('common.actual') }}
                            </div>
                            <div class="chart-legend-item">
                                <span class="chart-legend-dot dot-accent" />
                                {{ $t('common.ideal') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { db } from "@/utils/Firebase";
import { saveWorkbook } from "@/utils/Export";
import { mapActions, mapState } from "vuex";
import RadarChart from "@/components/common/RadarChart";
import EvaluationDatesChart from '../common/EvaluationDatesChart.vue';
import EvaluationSummaryChart from '../common/EvaluationSummaryChart.vue';
import SkeletonLoader from '../common/SkeletonLoader.vue';
import SegmentedTabs from '../common/SegmentedTabs.vue';
import { getCurrentValuesDatesForEvaluation, getDatasetsCompareForEvaluation, getDatasetsForEvaluation, getItemsForEvaluation, getValuesForEvaluation, getValuesGroupedForEvaluation } from "../../utils/Evaluations";

export default {
    name: "EvaluationStatisticsTeam",
    components: { RadarChart, EvaluationDatesChart, EvaluationSummaryChart, SkeletonLoader, SegmentedTabs },
    data() {
        return {
            loading: true,
            group: null,
            allStudents: [],
            student: null,
            assignedEvaluation: null,
            currentConfig: null,
            currentConfigDates: null,
            currentCompareConfig: null,
            view: 'group',
        }
    },
    computed: {
        ...mapState(['evaluations', 'teams', 'libraryCoach']),
        coachId(vm = this) {
            return vm.isCompany ? vm.libraryCoach?.id || null : vm.currentUser?.uid || null
        },
        hasValues(vm = this) {
            return vm.assignedEvaluation?.values?.length > 0 || false
        },
        teamId(vm = this) {
            return vm.$route.params.id
        },
        studentId(vm = this) {
            return vm.student?.id || null
        },
        evaluationId(vm = this) {
            return vm.assignedEvaluation?.evaluationId || null
        },
        currentEvaluation(vm = this) {
            if (!vm.evaluationId) return null
            return vm.evaluations.find(eva => eva.id === vm.evaluationId) || null
        },
        config(vm = this) {
            return vm.currentEvaluation?.config || []
        },
        configWithData(vm = this) {
            return vm.config.filter(c => vm.items.some(item => item.id === c.id))
        },
        hasData(vm = this) {
            return vm.items.length > 0
        },
        valuesGrouped(vm = this) {
            let values = vm.assignedEvaluation?.values || []
            return getValuesGroupedForEvaluation(values, vm.studentId, vm.group)
        },
        values(vm = this) {
            if (!vm.hasValues) return []
            return getValuesForEvaluation(vm.valuesGrouped)
        },
        items(vm = this) {
            if (!vm.config || vm.values.length === 0) return []
            return getItemsForEvaluation(vm.config, vm.values)
        },
        currentItem(vm = this) {
            if (!vm.currentConfig) return null
            return vm.items.find(item => item.id === vm.currentConfig.id) || null
        },
        currentValuesDates(vm = this) {
            if (!vm.currentConfigDates || vm.values.length === 0) return null
            return getCurrentValuesDatesForEvaluation(vm.valuesGrouped, vm.currentConfigDates)
        },
        evaluationsName(vm = this) {
            return vm.items.map(item => item.name)
        },
        evaluationData(vm = this) {
            if (vm.items.length === 0) return [];
            return getDatasetsForEvaluation(vm.items)
        },
        team(vm = this) {
            if (!vm.teamId) return null
            return vm.teams?.find(team => team.id === vm.teamId) || null
        },
        groups(vm = this) {
            return vm.team?.evaluationGroups || []
        },
        students(vm = this) {
            if (vm.group) return vm.allStudents.filter(student => vm.group.students.includes(student.id))
            if (!vm.isCompare) return []
            const ids = new Set()
            vm.groups.forEach(group => (group.students || []).forEach(id => ids.add(id)))
            return vm.allStudents.filter(student => ids.has(student.id))
        },
        isCompare(vm = this) {
            return vm.view === 'compare'
        },
        compareScopeLabel(vm = this) {
            return vm.group?.name || vm.team?.name || vm.$t('common.group')
        },
        viewOptions(vm = this) {
            return [
                { name: vm.$t('common.group_statistics'), value: 'group' },
                { name: vm.$t('common.student_vs_group'), value: 'compare' },
            ]
        },
        compareStudentItems(vm = this) {
            if (!vm.isCompare || !vm.studentId || !vm.hasValues) return []
            const values = vm.assignedEvaluation?.values || []
            const grouped = getValuesGroupedForEvaluation(values, vm.studentId, vm.group)
            return getItemsForEvaluation(vm.config, getValuesForEvaluation(grouped))
        },
        compareGroupItems(vm = this) {
            if (!vm.isCompare || !vm.hasValues) return []
            const values = vm.assignedEvaluation?.values || []
            const grouped = getValuesGroupedForEvaluation(values, null, vm.group)
            return getItemsForEvaluation(vm.config, getValuesForEvaluation(grouped))
        },
        compareRows(vm = this) {
            return vm.compareStudentItems.map(item => {
                const groupItem = vm.compareGroupItems.find(group => group.id === item.id) || null
                const studentValue = Number(item.actual)
                const groupValue = groupItem ? Number(groupItem.actual) : null
                const better = item.upgrade ? studentValue >= groupValue : studentValue <= groupValue

                return {
                    id: item.id,
                    name: item.name,
                    unit: item.unit,
                    student: studentValue,
                    group: groupValue,
                    difference: groupValue ? ((studentValue - groupValue) / groupValue) * 100 : null,
                    color: better ? 'success' : 'error',
                }
            })
        },
        compareConfigWithData(vm = this) {
            return vm.config.filter(config => vm.compareRows.some(row => row.id === config.id))
        },
        compareNames(vm = this) {
            return vm.compareStudentItems.map(item => item.name)
        },
        compareDatasets(vm = this) {
            if (!vm.compareStudentItems.length) return []
            return getDatasetsCompareForEvaluation(vm.compareStudentItems, vm.compareGroupItems)
        },
        compareStudentValuesDates(vm = this) {
            if (!vm.currentCompareConfig || !vm.studentId) return null
            const values = vm.assignedEvaluation?.values || []
            const grouped = getValuesGroupedForEvaluation(values, vm.studentId, vm.group)
            return getCurrentValuesDatesForEvaluation(grouped, vm.currentCompareConfig)
        },
        compareGroupValuesDates(vm = this) {
            if (!vm.currentCompareConfig) return null
            const values = vm.assignedEvaluation?.values || []
            const grouped = getValuesGroupedForEvaluation(values, null, vm.group)
            return getCurrentValuesDatesForEvaluation(grouped, vm.currentCompareConfig)
        },
        teamInitials(vm = this) {
            const name = vm.team?.name || ''
            return name.trim().substring(0, 3).toUpperCase() || '—'
        },
        memberCount(vm = this) {
            const ids = new Set()
            vm.groups.forEach(g => (g.students || []).forEach(id => ids.add(id)))
            return ids.size
        },
        recordsCount(vm = this) {
            return vm.assignedEvaluation?.values?.length || 0
        },
        lastEvaluationDate(vm = this) {
            const values = vm.assignedEvaluation?.values || []
            const dates = values.map(v => vm.$moment(v.date, 'DD-MM-YYYY')).filter(d => d.isValid())
            if (!dates.length) return vm.$t('common.not_available_short')
            return dates.reduce((a, b) => b.isAfter(a) ? b : a).format('DD/MM/YYYY')
        },
    },
    async mounted() {
        const vm = this
        await vm.withLoading(async () => {
            if (vm.evaluations.length === 0) await vm.bindEvaluations(vm.coachId)
            await vm.loadStudents()
            await vm.loadAssignedEvaluation()
        })
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        async loadStudents() {
            const vm = this
            vm.allStudents = []

            let studentsRef = await db.collection('users')
                .where('studentSubscription.coachId', '==', vm.coachId)
                .orderBy('name')
                .get()

            vm.allStudents = studentsRef.docs.map(student => ({
                ...student.data(),
                id: student.id,
                img: student.data().avatar || vm.imgPlaceholder,
            }))
        },
        async loadAssignedEvaluation() {
            const vm = this
            let assignedRef = await db.collection('assignedEvaluations').doc(vm.team.assignedEvaluationId).get()
            vm.assignedEvaluation = { id: assignedRef.id, ...assignedRef.data() }
        },
        selectGroup(group) {
            this.group = group
            this.student = null
        },
        async exportExcel() {
            const vm = this
            const XLSX = (await import("xlsx")).default
            let evaluationData = vm.valuesGrouped.map(value => {
                return {
                    [vm.$t('common.date')]: value.date,
                    ...Object.fromEntries(vm.config.map(config => {
                        const val = value[config.id] != null ? parseFloat(value[config.id].toString().replace(',', '.')) : null
                        return [config.name, val]
                    }))
                }
            })
            let data = XLSX.utils.json_to_sheet(evaluationData)
            const workbook = XLSX.utils.book_new()
            const scope = vm.student?.name || vm.group?.name || vm.team?.name || ''
            const filename = scope ? `${vm.$t('common.evaluations')} - ${scope}` : vm.$t('common.evaluations')
            XLSX.utils.book_append_sheet(workbook, data, filename.substring(0, 31))
            await saveWorkbook(XLSX, workbook, filename, vm.$t('common.export'))
        },
        diffText(item) {
            return `${item.color === 'success' ? '+' : '−'}${item.difference}`
        },
        diffClass(item) {
            return item.color === 'success' ? 'diff-positive' : 'diff-negative'
        },
        diffBarStyle(item) {
            const magnitude = Math.min((parseFloat(item.difference) || 0) * 8, 100)
            return {
                width: `${magnitude}%`,
                background: item.color === 'success' ? 'var(--v-success-base)' : 'var(--v-error-base)',
            }
        },
        compareDiffText(row) {
            if (row.difference == null) return '—'
            const value = Math.abs(row.difference)
            const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)
            return `${row.difference >= 0 ? '+' : '−'}${formatted}%`
        },
        compareDiffClass(row) {
            if (row.difference == null) return null
            return row.color === 'success' ? 'diff-positive' : 'diff-negative'
        },
        compareDiffBarStyle(row) {
            const magnitude = Math.min(Math.abs(row.difference || 0) * 2, 100)
            return {
                width: `${magnitude}%`,
                background: row.color === 'success' ? 'var(--v-success-base)' : 'var(--v-error-base)',
            }
        },
        cmpBarStyle(row, key) {
            const max = Math.max(Number(row.student) || 0, Number(row.group) || 0)
            if (!max) return { width: '0%' }
            const value = key === 'student' ? Number(row.student) || 0 : Number(row.group) || 0
            const percent = Math.min((value / max) * 100, 100)
            return key === 'student' ? { width: `${percent}%` } : { left: `${percent}%` }
        },
    },
    watch: {
        configWithData(val) {
            const validConfig = this.currentConfig && val.some(c => c.id === this.currentConfig.id)
            if (!validConfig) this.currentConfig = val[0] || null
            const validDates = this.currentConfigDates && val.some(c => c.id === this.currentConfigDates.id)
            if (!validDates) this.currentConfigDates = val[0] || null
        },
        group() {
            this.student = null
        },
        compareConfigWithData(val) {
            const valid = this.currentCompareConfig && val.some(config => config.id === this.currentCompareConfig.id)
            if (!valid) this.currentCompareConfig = val[0] || null
        },
    }
}
</script>

<style scoped>
.eval-stats {
    padding: 16px 24px;
}

.stats-top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.stats-view-tabs {
    margin-bottom: 16px;
    min-width: 280px;
}

.stats-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    padding: 0 12px 0 8px;
    border-radius: 8px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface-base);
    color: var(--v-txtSecondary-base);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 16px;
    transition: background 0.12s;
    text-transform: capitalize;
}

.stats-back-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.stats-top {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 20px;
}

.group-profile-card {
    width: 240px;
    flex-shrink: 0;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

.group-card-banner {
    width: 100%;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #0D2018 0%, #0D1A2A 100%);
}

.group-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: var(--v-surface-base);
    border: 2px solid var(--v-accent-base);
    color: var(--v-accent-base);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 800;
}

.group-card-body {
    padding: 14px 16px;
}

.group-card-name {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin-bottom: 10px;
}

.group-card-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 5px 0;
    border-bottom: 1px solid var(--v-outline-base);
    font-size: 12px;
    min-height: 34px;
}

.group-card-field:last-of-type {
    border-bottom: none;
}

.group-card-field-label {
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.group-card-field-value {
    color: var(--v-txtPrimary-base);
    font-weight: 600;
    text-align: right;
}

.group-card-field-value.value-accent {
    color: var(--v-primary-base);
}

.theme--light .group-card-field-value.value-accent {
    color: var(--v-accent-base);
}

.group-card-select {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 130px;
    padding: 3px 8px;
    border-radius: 7px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: border-color 0.12s;
}

.group-card-select:hover {
    border-color: var(--v-primary-base);
}

.stats-export-btn {
    width: 100%;
    height: 34px;
    border-radius: 8px;
    margin-top: 12px;
    background: rgba(204, 255, 51, 0.08);
    border: 1px solid rgba(204, 255, 51, 0.18);
    color: var(--v-primary-base);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background 0.12s;
}

.stats-export-btn:hover {
    background: rgba(204, 255, 51, 0.16);
}

.theme--light .stats-export-btn {
    background: rgba(51, 153, 153, 0.08);
    border-color: rgba(51, 153, 153, 0.25);
    color: var(--v-accent-base);
}

.stats-empty-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 48px 16px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
    text-align: center;
}

.stats-table-card {
    flex: 1;
    min-width: 0;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

.stats-table-scroll {
    overflow-x: auto;
}

.stats-table {
    width: 100%;
    border-collapse: collapse;
}

.stats-table thead tr {
    border-bottom: 1px solid var(--v-outline-base);
}

.stats-table thead th {
    padding: 9px 14px;
    text-align: right;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.07em;
    background: linear-gradient(180deg, var(--v-surface2-base) 0%, var(--v-surface-base) 100%);
    white-space: nowrap;
}

.stats-table thead th:first-child {
    text-align: left;
}

.stats-table thead th:nth-child(2) {
    text-align: center;
}

.stats-table tbody tr {
    border-bottom: 1px solid var(--v-outline-base);
    transition: background 0.1s;
}

.stats-table tbody tr:last-child {
    border-bottom: none;
}

.stats-table tbody tr:hover {
    background: var(--v-surface2-base);
}

.stats-table td {
    padding: 10px 14px;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    text-align: right;
    vertical-align: middle;
    white-space: nowrap;
}

.stats-table td:first-child {
    text-align: left;
}

.stats-table td:nth-child(2) {
    text-align: center;
    color: var(--v-txtMuted-base);
    font-size: 11px;
}

.metric-name-cell strong {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.diff-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.diff-bar-wrap {
    width: 80px;
    height: 4px;
    background: var(--v-outline2-base);
    border-radius: 2px;
    overflow: hidden;
}

.diff-bar-fill {
    height: 100%;
    border-radius: 2px;
}

.diff-positive {
    color: var(--v-success-base);
    font-weight: 700;
    font-size: 12px;
}

.diff-negative {
    color: var(--v-error-base);
    font-weight: 700;
    font-size: 12px;
}

.stats-charts {
    display: grid;
    grid-template-columns: 1.35fr 1fr 0.85fr;
    gap: 14px;
}

.chart-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

.chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 14px 4px 14px;
}

.chart-title {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    color: var(--v-primary-base);
}

.theme--light .chart-title {
    color: var(--v-accent-base);
}

.chart-select {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: none;
    border: none;
    outline: none;
    color: var(--v-primary-base);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    padding: 0;
    max-width: 130px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.theme--light .chart-select {
    color: var(--v-accent-base);
}

.chart-select-caret {
    font-size: 9px;
    line-height: 1;
}

.chart-body {
    padding: 4px 12px 12px 12px;
}

.chart-legend-row {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 6px;
}

.chart-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.chart-legend-dot {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    flex-shrink: 0;
}

.dot-success {
    background: var(--v-success-base);
}

.dot-primary {
    background: var(--v-primary-base);
}

.dot-accent {
    background: var(--v-accent-base);
}

.dot-secondary {
    background: var(--v-secondary-base);
}

.cmp-bars {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 230px;
    overflow-y: auto;
    padding-right: 4px;
}

.cmp-bar-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
}

.cmp-bar-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cmp-bar-values {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.cmp-bar-values b {
    color: var(--v-txtPrimary-base);
    font-weight: 700;
}

.cmp-bar-track {
    position: relative;
    height: 8px;
    border-radius: 4px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    overflow: hidden;
}

.cmp-bar-fill {
    height: 100%;
    border-radius: 4px;
    background: var(--v-primary-base);
}

.theme--light .cmp-bar-fill {
    background: var(--v-accent-base);
}

.cmp-bar-marker {
    position: absolute;
    top: -2px;
    bottom: -2px;
    width: 2px;
    background: var(--v-secondary-base);
}

@media (max-width: 1100px) {
    .stats-charts {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 860px) {
    .stats-top {
        flex-direction: column;
    }

    .group-profile-card {
        width: 100%;
    }
}

@media (max-width: 700px) {
    .stats-charts {
        grid-template-columns: 1fr;
    }
}
</style>
